import React, { useState, useEffect, useRef } from 'react';
import { X, User, Award, Briefcase, FileText, Loader2, Save, Upload, Image as ImageIcon } from 'lucide-react';
import {
  createTeamMember,
  updateTeamMember,
  uploadMemberImage,
  SchoolTeamMember,
  CreateTeamMemberRequest
} from '@/actions/school-team';
import { showToast } from '@/components/ui/Toast';

interface TeamMemberModalProps {
  slateId: string;
  member: SchoolTeamMember | null;
  onClose: (success?: boolean) => void;
}

const TeamMemberModal: React.FC<TeamMemberModalProps> = ({
  slateId,
  member,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<CreateTeamMemberRequest>({
    member_name: '',
    member_designation: '',
    member_qualification: '',
    member_description: '',
    member_image: '',
    member_order: 0
  });

  useEffect(() => {
    if (member) {
      setFormData({
        member_name: member.member_name || '',
        member_designation: member.member_designation || '',
        member_qualification: member.member_qualification || '',
        member_description: member.member_description || '',
        member_image: member.member_image || '',
        member_order: member.member_order || 0
      });

      // Set image preview if member has an image
      if (member.member_image && member.member_image !== 'member_thumb.png') {
        setImagePreview(member.member_image);
      }
    }
  }, [member]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      showToast('Image size should be less than 2MB', 'error');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload immediately
    try {
      setUploading(true);
      const response = await uploadMemberImage(slateId, file);

      if (response.success || response.status === 'success') {
        const imageUrl = response.data?.url || response.data?.filename;
        setFormData((prev) => ({
          ...prev,
          member_image: imageUrl
        }));
        showToast('Image uploaded successfully', 'success');
      } else {
        throw new Error(response.message || 'Failed to upload image');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      showToast(error.message || 'Failed to upload image', 'error');
      setImagePreview(member?.member_image || '');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview('');
    setFormData((prev) => ({
      ...prev,
      member_image: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'member_order' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.member_name.trim()) {
      showToast('Member name is required', 'error');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        member_name: formData.member_name.trim(),
        member_designation: formData.member_designation?.trim() || undefined,
        member_qualification: formData.member_qualification?.trim() || undefined,
        member_description: formData.member_description?.trim() || undefined,
        member_image: formData.member_image?.trim() || undefined
      };

      let response;
      if (member) {
        response = await updateTeamMember(slateId, member.member_id, payload);
      } else {
        response = await createTeamMember(slateId, payload);
      }

      if (response.success || response.status === 'success') {
        showToast(
          member ? 'Team member updated successfully' : 'Team member created successfully',
          'success'
        );
        onClose(true);
      } else {
        showToast(response.message || 'Failed to save team member', 'error');
      }
    } catch (error: any) {
      console.error('Error saving team member:', error);
      showToast(error.message || 'Failed to save team member', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => !loading && onClose()}
      />

      {/* Modal */}
      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-2xl">
          <div className="h-full flex flex-col bg-white dark:bg-gray-800 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <User className="w-6 h-6 text-brandColor" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {member ? 'Edit Team Member' : 'Add Team Member'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {member
                      ? 'Update team member information'
                      : 'Add a new member to your school team'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => !loading && onClose()}
                disabled={loading}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Form Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Member Name */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <User className="w-4 h-4 mr-2 text-gray-400" />
                    Member Name *
                  </label>
                  <input
                    type="text"
                    name="member_name"
                    value={formData.member_name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
                    placeholder="Enter member name"
                  />
                </div>

                {/* Designation */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                    Designation
                  </label>
                  <input
                    type="text"
                    name="member_designation"
                    value={formData.member_designation}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
                    placeholder="e.g., Principal, Vice Principal, Head of Department"
                  />
                </div>

                {/* Qualification */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Award className="w-4 h-4 mr-2 text-gray-400" />
                    Qualification
                  </label>
                  <input
                    type="text"
                    name="member_qualification"
                    value={formData.member_qualification}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
                    placeholder="e.g., M.Ed., Ph.D., B.Ed."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FileText className="w-4 h-4 mr-2 text-gray-400" />
                    Description
                  </label>
                  <textarea
                    name="member_description"
                    value={formData.member_description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent resize-none"
                    placeholder="Brief description about the team member..."
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <ImageIcon className="w-4 h-4 mr-2 text-gray-400" />
                    Member Image
                  </label>

                  {imagePreview ? (
                    <div className="space-y-3">
                      <div className="relative w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        {!uploading && (
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {uploading && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-white animate-spin" />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                      >
                        Change Image
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-48 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-brandColor hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <Upload className="w-12 h-12 text-gray-400 mb-3" />
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Click to upload image
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        PNG, JPG up to 2MB
                      </p>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Display Order */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    name="member_order"
                    value={formData.member_order}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Lower numbers appear first. You can also drag and drop to reorder.
                  </p>
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800">
              <form onSubmit={handleSubmit}>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => onClose()}
                    disabled={loading || uploading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || uploading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brandColor hover:bg-brand-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {member ? 'Updating...' : 'Creating...'}
                      </>
                    ) : uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {member ? 'Update Member' : 'Create Member'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberModal;
